import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  SortAsc,
  SortDesc,
  RefreshCw,
  Download
} from 'lucide-react';

const DiseaseTable = () => {
  // State for data
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for pagination
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0
  });

  // State for sorting
  const [sorting, setSorting] = useState({
    sortBy: 'HDOID',
    sortDir: 'asc'
  });

  // State for filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [consensusOnly, setConsensusOnly] = useState(false);

  // State for consensus info
  const [consensusInfo, setConsensusInfo] = useState({
    totalConsensusGenes: 0,
    totalGenes: 0,
    overallPercentage: 0
  });

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch data when pagination, sorting, or filtering changes
  useEffect(() => {
    fetchDiseases();
  }, [
    pagination.page,
    pagination.pageSize,
    sorting.sortBy,
    sorting.sortDir,
    debouncedSearchTerm,
    consensusOnly
  ]);

  // Function to fetch diseases
  const fetchDiseases = async () => {
    try {
      setLoading(true);

      // Construct query parameters
      const params = new URLSearchParams({
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sorting.sortBy,
        sortDir: sorting.sortDir,
        search: debouncedSearchTerm,
        consensusOnly: consensusOnly
      });

      // Fetch data from API
      const response = await fetch(`/api/disease/table?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setDiseases(data.data);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages
        }));
        setConsensusInfo(data.consensus);
      } else {
        throw new Error(data.message || 'Failed to fetch disease data');
      }

    } catch (err) {
      setError(err.message);
      console.error('Error fetching disease data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle sort change
  const handleSort = (column) => {
    setSorting(prev => ({
      sortBy: column,
      sortDir: prev.sortBy === column && prev.sortDir === 'asc' ? 'desc' : 'asc'
    }));

    // Reset to first page when sorting changes
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Handle consensus filter
  const handleConsensusChange = (e) => {
    setConsensusOnly(e.target.checked);
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Export as CSV
  const exportToCsv = () => {
    if (diseases.length === 0) return;

    // Create CSV header
    const headers = [
      'HDOID',
      'Description',
      'Gene Count',
      'Consensus Gene Count',
      'Consensus %',
      'Mutation Count',
      'Paper Count'
    ];

    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...diseases.map(disease => [
        disease.hdoid,
        `"${disease.description || ''}"`,
        disease.geneCount || 0,
        disease.consensusGeneCount || 0,
        disease.consensusPercentage || 0,
        disease.mutationCount || 0,
        disease.paperCount || 0
      ].join(','))
    ];

    // Create and download CSV file
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', 'disease_table.csv');
    link.click();

    URL.revokeObjectURL(url);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sorting.sortBy !== column) {
      return <SortAsc className="ml-1 h-4 w-4 text-gray-400" />;
    }

    return sorting.sortDir === 'asc'
      ? <SortAsc className="ml-1 h-4 w-4" />
      : <SortDesc className="ml-1 h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Diseases Catalog
          </h1>
          <div className="flex flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0">
            <div className="flex items-center relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-2 pr-8 py-1 border border-gray-300 rounded-md text-sm max-w-xs"
              />
              <Search className="absolute right-2 h-4 w-4 text-gray-500" />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="consensus"
                checked={consensusOnly}
                onChange={handleConsensusChange}
                className="rounded text-blue-600"
              />
              <label
                htmlFor="consensus"
                className="text-sm font-medium leading-none"
              >
                With Consensus Genes Only
              </label>
            </div>

            <button
              onClick={() => fetchDiseases()}
              className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>

            <button
              onClick={exportToCsv}
              className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={diseases.length === 0}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Content */}
        {error ? (
          <div className="rounded-md bg-red-50 p-4 my-4">
            <div className="flex">
              <div className="text-red-700">
                <p className="text-sm">Error loading disease data: {error}</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {consensusInfo.totalConsensusGenes > 0 && (
              <div className="rounded-md bg-blue-50 p-4 mb-4">
                <div className="flex">
                  <div className="text-blue-700">
                    <p className="text-sm">
                      <strong>Consensus Genes:</strong> {consensusInfo.totalConsensusGenes} of {consensusInfo.totalGenes} genes ({consensusInfo.overallPercentage}%) across all diseases are consensus genes
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white shadow rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('HDOID')}
                      >
                        <div className="flex items-center">
                          Human Disease Ontology ID {renderSortIcon('HDOID')}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('Description')}
                      >
                        <div className="flex items-center">
                          Disease {renderSortIcon('Description')}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Genes
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Consensus %
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Mutations
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Papers
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center py-6">
                            <RefreshCw className="h-6 w-6 animate-spin text-gray-500" />
                            <span className="ml-2">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : diseases.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-center py-6 text-gray-500">
                          No disease records found
                        </td>
                      </tr>
                    ) : (
                      diseases.map(disease => (
                        <tr key={disease.hdoid} className={disease.consensusPercentage > 50 ? 'bg-blue-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {disease.hdoid ? (
                              <a
                                href={`/disease-search?q=${encodeURIComponent(disease.hdoid)}`}
                                className="text-gray-900 hover:text-blue-600 transition-colors duration-200"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {disease.hdoid}
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{disease.description || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="inline-flex items-center bg-gray-100 px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800">
                              {disease.geneCount}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${disease.consensusPercentage > 50 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {disease.consensusPercentage}% ({disease.consensusGeneCount}/{disease.geneCount})
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="inline-flex items-center bg-gray-100 px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800">
                              {disease.mutationCount}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="inline-flex items-center bg-gray-100 px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800">
                              {disease.paperCount}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500">
                Showing {diseases.length > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0}-
                {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{" "}
                {pagination.total} results
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1 || loading}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <span className="text-sm">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || loading}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages || loading}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DiseaseTable;